import { IonIcon, useIonRouter } from "@ionic/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  clipboardOutline,
  flagOutline,
  helpCircleOutline,
  hourglassOutline,
  locationOutline,
  timeOutline,
} from "ionicons/icons";
import { toast } from "sonner";
import { useQRScanner } from "@/components/global/qr-scanner/hooks/use-qr-scanner";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { ProjectImage } from "@/components/molecules/project/project-image";
import { HoursFormModal } from "@/components/molecules/shift/hours-form-modal";
import { useBoomerForm } from "@/form/boomer/hook";
import { useValidator } from "@/form/common/validator";
import { api } from "@/lib/backend";
import { type Shift } from "@/lib/backend/api";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { useFeatureFlags } from "@/lib/backend/utils/hooks/use-feature-flags";
import { MINIMUM_TIME_BEFORE_CLOCK_IN_MINUTES } from "@/lib/constants";
import { useHoursInfoSections } from "@/lib/constants/application-info";
import { ExportIcon } from "@/lib/icons/export.icon";
import { timeIcon } from "@/lib/icons/time.icon";
import { Routes } from "@/lib/router";
import { openFile } from "@/lib/utils/download";
import { logger } from "@/logger";
import { formatInTz, formatTime, formatTimeRange } from "@/utils/format-datetime";
import {
  durationToMinutes,
  formatDurationToTimeString,
  minutesToDuration,
  parseTimeString,
} from "@/utils/time";

type ShiftCardOverviewProps = {
  shift: Shift;
  /**
   * `list` (default) renders the card as one row in a dated list: a date column on the left and a
   * separator underneath. `hero` drops both, because the Vandaag screen already states the date and
   * the countdown above the card and a second date reads as a duplicate.
   */
  variant?: "list" | "hero";
};

export function UpcomingShiftCardRework({ shift, variant = "list" }: ShiftCardOverviewProps) {
  const isHero = variant === "hero";
  const { t } = useTranslation();
  const hoursInfoSections = useHoursInfoSections();
  const router = useIonRouter();
  const queryClient = useQueryClient();
  const modalClockIn = useRef<HTMLIonModalElement>(null);
  const modalClockOut = useRef<HTMLIonModalElement>(null);
  const modalClockInInfo = useRef<HTMLIonModalElement>(null);
  const modalClockOutInfo = useRef<HTMLIonModalElement>(null);
  const modalHoursRef = useRef<HTMLIonModalElement>(null);
  const hoursInfoModalRef = useRef<HTMLIonModalElement>(null);

  const validator = useValidator();

  // Alles op deze kaart staat in projecttijd, nooit in die van het toestel: een dienst van
  // 15:00-23:00 las op een telefoon in Toronto als 09:00-17:00 en de datum ernaast viel een dag
  // terug. Dat geldt ook voor de voorgevulde uren in het formulier — die gaan als "HH:mm" terug
  // naar de server, die ze in projecttijd leest.
  const timeZone = shift.timeZone ?? shift.project?.timeZone;

  const { data: featureFlags } = useFeatureFlags();
  const shiftSwapEnabled = featureFlags?.shiftSwapEnabled ?? false;

  // Fetch documents for this project to get the briefing (first document)
  const { data: documents } = useQuery({
    queryKey: ["documents", shift.projectId],
    queryFn: () => extractionWrapper(api.document.getAll("project", shift.projectId!)),
    enabled: !!shift.projectId,
  });

  const briefingDocument = documents?.items?.[0];

  // QR Clock In Mutation
  const qrClockInMutation = useMutation({
    mutationKey: ["shift", shift.id, "qr-clock-in"],
    mutationFn: async (qrCodeData: string) => {
      if (!shift.project?.id) {
        throw new Error("Project ID is required");
      }
      return await extractionWrapper(
        api.shiftDeclaration.clockIn({
          projectId: shift.project.id,
          qrCodeData,
          shiftId: shift.id,
        }),
      );
    },
    onMutate: () => {
      // Geen toast.loading — zie `sonner.guard.test.ts`. De knop toont de voortgang al.
      toast.dismiss(`shift-${shift.id}-clock`);
    },
    onSuccess: async () => {
      toast.success(t("shift:clock-in.success"), {
        id: `shift-${shift.id}-clock`,
      });
      await queryClient.refetchQueries({ queryKey: ["shifts"] });
      modalClockIn.current?.dismiss();
    },
    onError: (error: any) => {
      const message = error?.message || t("shift:clock-in.error");
      toast.error(message, { id: `shift-${shift.id}-clock` });
      logger.error("QR clock-in error", error);
    },
  });

  const handleDownload = async (fileName: string) => {
    try {
      // Properly encode the document name for use in the URL path
      const encodedFileName = encodeURIComponent(fileName);
      const response = await api.file.getProjectDocumentUrl(shift.projectId!, encodedFileName);
      openFile(response.data.url);
    } catch (error) {
      logger.error("Failed to download briefing:", error);
      toast.error(t("shift:actions.briefing-error"));
    }
  };

  // QR Clock Out Mutation
  const qrClockOutMutation = useMutation({
    mutationKey: ["shift", shift.id, "qr-clock-out"],
    mutationFn: async (qrCodeData: string) => {
      if (!shift.project?.id) {
        throw new Error("Project ID is required");
      }
      return await extractionWrapper(
        api.shiftDeclaration.clockOut({
          projectId: shift.project.id,
          qrCodeData,
          shiftId: shift.id,
        }),
      );
    },
    onMutate: () => {
      // Geen toast.loading — zie `sonner.guard.test.ts`. De knop toont de voortgang al.
      toast.dismiss(`shift-${shift.id}-clock`);
    },
    onSuccess: async () => {
      toast.success(t("shift:clock-out.success"), {
        id: `shift-${shift.id}-clock`,
      });
      await queryClient.refetchQueries({ queryKey: ["shifts"] });
      modalClockOut.current?.dismiss();
    },
    onError: (error: any) => {
      const message = error?.message || t("shift:clock-out.error");
      toast.error(message, { id: `shift-${shift.id}-clock` });
      logger.error("QR clock-out error", error);
    },
  });

  // QR Scanner hooks
  const {
    scanQRCode: scanClockIn,
    isScanning: isScanningClockIn,
    WebScannerComponent: ClockInWebScanner,
  } = useQRScanner({
    onScanSuccess: (data) => qrClockInMutation.mutate(data),
  });

  const {
    scanQRCode: scanClockOut,
    isScanning: isScanningClockOut,
    WebScannerComponent: ClockOutWebScanner,
  } = useQRScanner({
    onScanSuccess: (data) => qrClockOutMutation.mutate(data),
  });

  // A shift is its own screen now. Tapping the card opens the shift, not the project it
  // happens to belong to — the employee thinks "mijn dienst zaterdag", not "project X".
  const handleCardClick = () => {
    router.push(Routes.ShiftDetail({ id: shift.id }), "forward");
  };

  const handleClockIn = (e: React.MouseEvent) => {
    e.stopPropagation();
    modalClockIn.current?.present();
  };

  const handleConfirmClockIn = () => {
    scanClockIn();
  };

  const handleClockOut = (e: React.MouseEvent) => {
    e.stopPropagation();
    modalClockOut.current?.present();
  };

  const handleConfirmClockOut = () => {
    scanClockOut();
  };

  // Hours registration mutation for manual entry
  const hoursMutation = useMutation({
    mutationKey: ["shift", shift.id, "hours"],
    mutationFn: async (variables: {
      startTime: string;
      endTime: string;
      breakTime: number;
      comment?: string;
    }) => {
      // Free-text remark (issue #426): trim, and only send the field when the employee
      // actually typed something — empty/whitespace becomes null so it does not render a
      // (false) badge in Planning. Same normalisation as useCompletedShiftActions; this
      // path (a shift that has started but not ended) was the one place still dropping it.
      const trimmedComment = variables.comment?.trim() ?? "";
      await api.shiftDeclaration.updateFullEntry(shift.id, {
        startAt: variables.startTime,
        endAt: variables.endTime,
        breakTime: variables.breakTime,
        shiftId: shift.id,
        comment: trimmedComment === "" ? null : trimmedComment,
      });
    },
    onMutate: () => {
      // Geen toast.loading — zie `sonner.guard.test.ts`. De knop toont de voortgang al.
      toast.dismiss(`shift-${shift.id}-hours`);
    },
    onSuccess: () => {
      toast.success(t("actions:declare-hours.success"), {
        id: `shift-${shift.id}-hours`,
      });
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      queryClient.invalidateQueries({ queryKey: ["shift", shift.id] });
      setTimeout(() => {
        modalHoursRef.current?.dismiss();
      }, 100);
    },
    onError: (error) => {
      toast.error(t("actions:declare-hours.error"), {
        id: `shift-${shift.id}-hours`,
      });
      logger.error(error);
    },
  });

  // Prepare form with existing or planned times
  const existingStartAt =
    (typeof shift.shiftTimeEntry?.startAt === "string"
      ? shift.shiftTimeEntry.startAt
      : undefined) ?? shift.startAt;

  const existingEndAt =
    (typeof shift.shiftTimeEntry?.endAt === "string" ? shift.shiftTimeEntry.endAt : undefined) ??
    shift.endAt;

  const [formHours, { Element: FormElementHours }] = useBoomerForm({
    defaultValues: {
      startTime: existingStartAt ? formatTime(existingStartAt, { timeZone }) : "",
      endTime: existingEndAt ? formatTime(existingEndAt, { timeZone }) : "",
      breakDuration: formatDurationToTimeString(
        minutesToDuration(shift.shiftTimeEntry?.breakTimeMinutes ?? 0),
      ),
      // Optional free-text remark (issue #426); prefilled when re-editing.
      comment: shift.shiftTimeEntry?.comment ?? "",
    },
    onSubmit: async ({ value }) => {
      await hoursMutation.mutateAsync({
        ...value,
        breakTime: durationToMinutes(parseTimeString(value.breakDuration)),
      });
    },
  });

  const handleRegisterHours = (e: React.MouseEvent) => {
    e.stopPropagation();
    modalHoursRef.current?.present();
  };

  // Format month abbreviation (e.g., "Dec")
  const month = formatInTz(shift.startAt, { month: "short", timeZone });

  // Format day (e.g., "27")
  const day = formatInTz(shift.startAt, { day: "numeric", timeZone });

  // Format weekday abbreviation (e.g., "Wo")
  const weekday = formatInTz(shift.startAt, { weekday: "short", timeZone });

  // Format time range (e.g., "12:00 - 18:00")
  const timeRange = formatTimeRange(shift.startAt, shift.endAt, { timeZone });

  // Check if user can clock in (within MINIMUM_TIME_BEFORE_CLOCK_IN_MINUTES before shift start)
  const canClockIn = () => {
    const now = new Date();
    const shiftStart = new Date(shift.startAt);
    const minutesUntilStart = (shiftStart.getTime() - now.getTime()) / (1000 * 60);
    return minutesUntilStart <= MINIMUM_TIME_BEFORE_CLOCK_IN_MINUTES;
  };

  // Check if shift has started (for manual hours registration)
  const hasShiftStarted = () => {
    const now = new Date();
    const shiftStart = new Date(shift.startAt);
    return now >= shiftStart;
  };

  // Check if hours can be registered manually (for non-QR projects, after shift start)
  const canRegisterHoursManually =
    hasShiftStarted() &&
    !shift.project?.enableQRCodeClocking &&
    shift.project?.allowEmployeeSubmitHours &&
    shift.project?.status !== "FINALIZED";

  return (
    <>
      <div onClick={handleCardClick} className="flex w-full cursor-pointer items-start gap-5">
        {/* Date badge */}
        {!isHero && (
          <div className="flex w-12 shrink-0 flex-col items-center px-1 py-1 capitalize">
            <div className="flex flex-col items-center pb-0.5 text-center text-txt-primary">
              <p className="text-sm font-semibold leading-[1.4]">{month}</p>
              <p className="text-2xl font-semibold leading-[1.2]">{day}</p>
            </div>
            <p className="text-sm font-medium leading-[1.4] text-txt-tertiary">{weekday}</p>
          </div>
        )}

        {/* Shift details */}
        <div
          className={
            isHero
              ? "flex flex-1 items-start gap-5"
              : "flex flex-1 items-start gap-5 border-b border-solid border-bordercolor-primary"
          }
        >
          <div className="flex flex-1 flex-col gap-4 pb-5">
            {/* Het beeld en de naam.

                Op de hero staan ze naast elkaar. Dat is de bovenste kaart van Vandaag, en met een
                poster van 16:9 over de volle breedte stond het antwoord op "hoe laat en waar" --
                waarvoor je dit scherm opent -- onder de vouw. Het kader blijft 16:9 (elk kader is
                dat, zie `project-image.guard.test.ts`), dus kleiner betekent hier smaller en niet
                platter: een plat kader van de volle breedte zou weer gaan bijsnijden.

                In de lijst eronder blijft het beeld over de volle breedte staan. Daar is het niet
                de kop van een kaart maar de kaart zelf, en daar is de hoogte geen probleem: wat
                daar staat heb je al scrollend gevonden. */}
            {isHero ? (
              <div className="flex items-start gap-3">
                <ProjectImage
                  projectId={shift.projectId}
                  imageKey={shift.project?.image}
                  alt={shift.project?.name || "Project"}
                  className="aspect-video w-[120px] shrink-0 rounded-xl"
                />

                <div className="flex min-w-0 flex-col">
                  <p className="text-xs font-bold uppercase tracking-wide text-txt-secondary">
                    DIENST
                  </p>
                  <h3 className="overflow-hidden text-ellipsis text-lg font-semibold leading-[1.6] text-txt-primary">
                    {shift.project?.name}
                  </h3>
                </div>
              </div>
            ) : (
              <>
                <ProjectImage
                  projectId={shift.projectId}
                  imageKey={shift.project?.image}
                  alt={shift.project?.name || "Project"}
                  className="aspect-video w-full rounded-xl"
                />

                <div className="flex flex-col">
                  <p className="text-xs font-bold uppercase tracking-wide text-txt-secondary">
                    DIENST
                  </p>
                  <h3 className="overflow-hidden text-ellipsis text-lg font-semibold leading-[1.6] text-txt-primary">
                    {shift.project?.name}
                  </h3>
                </div>
              </>
            )}

            {/* Details with icons */}
            <div className="flex flex-col gap-2">
              {/* User function */}
              {shift.userFunction?.name && (
                <div className="flex items-center gap-1.5">
                  <IonIcon icon={clipboardOutline} className="size-5 text-txt-tertiary" />
                  <p className="text-md font-medium leading-[1.5] text-txt-tertiary">
                    {shift.userFunction.name}
                  </p>
                </div>
              )}

              {/* Position */}
              {shift.position?.name && (
                <div className="flex items-center gap-1.5">
                  <IonIcon icon={flagOutline} className="size-5 text-txt-tertiary" />
                  <p className="text-md font-medium leading-[1.5] text-txt-tertiary">
                    {shift.position.name}
                  </p>
                </div>
              )}

              {/* Address */}
              {shift.project?.addressLine && (
                <div className="flex items-center gap-1.5">
                  <IonIcon icon={locationOutline} className="size-5 text-txt-tertiary" />
                  <p className="text-md font-medium leading-[1.5] text-txt-tertiary">
                    {shift.project.addressLine}
                  </p>
                </div>
              )}

              {/* Time */}
              <div className="flex items-center gap-1.5">
                <IonIcon icon={timeOutline} className="size-5 text-txt-tertiary" />
                <p className="text-md font-medium leading-[1.5] text-txt-tertiary">{timeRange}</p>
              </div>

              {/* Cancellation request pending status */}
              {shift.stats?.hasPendingCancellation && (
                <div className="flex items-center gap-1.5">
                  <IonIcon icon={hourglassOutline} className="size-5 text-warning-120" />
                  <p className="text-md font-medium leading-[1.5] text-warning-120">
                    {t("shift:status.cancellation-pending")}
                  </p>
                </div>
              )}

              {/* Transfer request pending status - clickable to view/share */}
              {shiftSwapEnabled &&
                shift.stats?.hasPendingTransfer &&
                !shift.stats?.hasPendingCancellation && (
                  <div className="flex items-center gap-1.5">
                    <ExportIcon className="size-5 text-warning-120" />
                    <span className="text-md font-medium leading-[1.5] text-warning-120">
                      {t("shift:status.transfer-pending")}
                    </span>
                  </div>
                )}
            </div>

            {/* Action buttons */}
            <div className="flex w-full flex-col gap-2">
              {/* Briefing button - shows if there's at least one document */}
              {briefingDocument && (
                <button
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    handleDownload(briefingDocument.name);
                  }}
                  className="flex w-full items-center justify-center gap-1.5 rounded-xl border-2 border-solid border-button-primary bg-button-primary px-4 py-3"
                >
                  <p className="text-md font-semibold leading-[1.5] tracking-[0.08px] text-txt-inverted">
                    {t("shift:actions.briefing")}
                  </p>
                </button>
              )}

              {/* Route button */}
              {shift.project?.addressLine && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const address = encodeURIComponent(shift.project?.addressLine || "");
                    window.open(
                      `https://www.google.com/maps/search/?api=1&query=${address}`,
                      "_blank",
                    );
                  }}
                  className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-solid border-bordercolor-primary px-4 py-3"
                >
                  <p className="text-md font-semibold leading-[1.5] tracking-[0.08px] text-txt-primary">
                    {t("shift:actions.view-route")}
                  </p>
                </button>
              )}

              {/* Clock In / Clock Out buttons - only show if QR clocking is enabled */}
              {shift.project?.enableQRCodeClocking && (
                <>
                  {/* Clock In button - only show if not clocked in yet and within time window */}
                  {!shift.shiftTimeEntry?.startAt && canClockIn() && (
                    <button
                      type="button"
                      onClick={handleClockIn}
                      className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-brand-500 px-4 py-3"
                    >
                      <p className="text-md font-semibold leading-[1.5] tracking-[0.08px] text-white">
                        {t("shift:clock-in.action")}
                      </p>
                      <IonIcon icon={timeIcon} className="size-4 text-white" />
                    </button>
                  )}

                  {/* Clock Out button - only show if clocked in but not clocked out */}
                  {shift.shiftTimeEntry?.startAt && !shift.shiftTimeEntry?.endAt && (
                    <button
                      type="button"
                      onClick={handleClockOut}
                      className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-brand-500 px-4 py-3"
                    >
                      <p className="text-md font-semibold leading-[1.5] tracking-[0.08px] text-white">
                        {t("shift:clock-out.action")}
                      </p>
                      <IonIcon icon={timeIcon} className="size-4 text-white" />
                    </button>
                  )}
                </>
              )}

              {/* Manual hours registration button - only for non-QR projects after shift starts */}
              {canRegisterHoursManually && (
                <button
                  type="button"
                  onClick={handleRegisterHours}
                  className="flex w-full items-center justify-center gap-1.5 rounded-xl border-2 border-solid border-button-primary bg-button-primary px-4 py-3"
                >
                  <p className="text-md font-semibold leading-[1.5] tracking-[0.08px] text-txt-inverted">
                    {shift.shiftTimeEntry?.id ? t("shift:hours.edit") : t("shift:hours.title")}
                  </p>
                  <IonIcon icon={timeIcon} className="size-4 text-txt-inverted" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal for Clock In */}
      <ModalSheetAutoHeight
        ref={modalClockIn}
        onDidDismiss={() => !isScanningClockIn && modalClockIn.current?.dismiss()}
      >
        <div className="flex flex-col gap-6 p-6">
          {/* Header with info icon */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-txt-primary">
              {t("shift:clock-in.confirm.title")}
            </h2>
            <button
              type="button"
              onClick={() => modalClockInInfo.current?.present()}
              className="flex size-10 items-center justify-center"
            >
              <IonIcon icon={helpCircleOutline} className="text-2xl text-txt-tertiary" />
            </button>
          </div>

          {/* Explainer content */}
          <div className="flex flex-col gap-4">
            <p className="text-sm leading-relaxed text-txt-secondary">
              {t("shift:clock-in.confirm.description")}
            </p>

            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold text-txt-primary">
                {t("shift:clock-in.confirm.steps-title")}
              </p>
              <ul className="flex list-inside list-disc flex-col gap-1.5 text-sm leading-relaxed text-txt-secondary">
                <li>{t("shift:clock-in.confirm.step1")}</li>
                <li>{t("shift:clock-in.confirm.step2")}</li>
                <li>{t("shift:clock-in.confirm.step3")}</li>
                <li>{t("shift:clock-in.confirm.step4")}</li>
              </ul>
            </div>
          </div>

          {/* Action button */}
          <button
            type="button"
            onClick={handleConfirmClockIn}
            disabled={qrClockInMutation.isPending || isScanningClockIn}
            className="w-full rounded-xl bg-button-primary py-3 text-center text-base font-semibold text-txt-inverted disabled:bg-surface-tertiary disabled:text-txt-tertiary"
          >
            {qrClockInMutation.isPending || isScanningClockIn
              ? t("shift:clock-in.loading")
              : t("shift:clock-in.confirm.button")}
          </button>
        </div>
      </ModalSheetAutoHeight>

      {/* Confirmation Modal for Clock Out */}
      <ModalSheetAutoHeight
        ref={modalClockOut}
        onDidDismiss={() => !isScanningClockOut && modalClockOut.current?.dismiss()}
      >
        <div className="flex flex-col gap-6 p-6">
          {/* Header with info icon */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-txt-primary">
              {t("shift:clock-out.confirm.title")}
            </h2>
            <button
              type="button"
              onClick={() => modalClockOutInfo.current?.present()}
              className="flex size-10 items-center justify-center"
            >
              <IonIcon icon={helpCircleOutline} className="text-2xl text-txt-tertiary" />
            </button>
          </div>

          {/* Explainer content */}
          <div className="flex flex-col gap-4">
            <p className="text-sm leading-relaxed text-txt-secondary">
              {t("shift:clock-out.confirm.description")}
            </p>

            <div className="flex flex-col gap-2">
              <p className="text-sm font-semibold text-txt-primary">
                {t("shift:clock-out.confirm.steps-title")}
              </p>
              <ul className="flex list-inside list-disc flex-col gap-1.5 text-sm leading-relaxed text-txt-secondary">
                <li>{t("shift:clock-out.confirm.step1")}</li>
                <li>{t("shift:clock-out.confirm.step2")}</li>
                <li>{t("shift:clock-out.confirm.step3")}</li>
                <li>{t("shift:clock-out.confirm.step4")}</li>
              </ul>
            </div>
          </div>

          {/* Action button */}
          <button
            type="button"
            onClick={handleConfirmClockOut}
            disabled={qrClockOutMutation.isPending || isScanningClockOut}
            className="w-full rounded-xl bg-button-primary py-3 text-center text-base font-semibold text-txt-inverted disabled:bg-surface-tertiary disabled:text-txt-tertiary"
          >
            {qrClockOutMutation.isPending || isScanningClockOut
              ? t("shift:clock-out.loading")
              : t("shift:clock-out.confirm.button")}
          </button>
        </div>
      </ModalSheetAutoHeight>

      {/* Hours Modal for manual entry */}
      <HoursFormModal
        modalRef={modalHoursRef}
        hoursInfoModalRef={hoursInfoModalRef}
        formHours={formHours}
        FormElementHours={FormElementHours}
        validator={validator}
        closeModal={() => modalHoursRef.current?.dismiss()}
      />

      {/* QR Scanners */}
      {ClockInWebScanner && <ClockInWebScanner />}
      {ClockOutWebScanner && <ClockOutWebScanner />}

      {/* Info Modal for Clock In */}
      <ModalSheetAutoHeight ref={modalClockInInfo}>
        <div className="flex flex-col gap-6 p-6">
          <h2 className="text-xl font-bold text-txt-primary">Over uren registratie</h2>

          <div className="flex flex-col gap-4">
            {hoursInfoSections.map((section, index) => (
              <div key={index} className="flex flex-col gap-2">
                <h3 className="text-base font-semibold text-txt-primary">{section.title}</h3>
                {Array.isArray(section.content) ? (
                  <ul className="flex flex-col gap-2">
                    {section.content.map((item, i) => (
                      <li key={i} className="text-sm leading-relaxed text-txt-secondary">
                        • {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm leading-relaxed text-txt-secondary">{section.content}</p>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => modalClockInInfo.current?.dismiss()}
            className="w-full rounded-xl bg-surface-tertiary py-3 text-center text-base font-semibold text-txt-primary"
          >
            {t("common:close")}
          </button>
        </div>
      </ModalSheetAutoHeight>

      {/* Info Modal for Clock Out */}
      <ModalSheetAutoHeight ref={modalClockOutInfo}>
        <div className="flex flex-col gap-6 p-6">
          <h2 className="text-xl font-bold text-txt-primary">Over uren registratie</h2>

          <div className="flex flex-col gap-4">
            {hoursInfoSections.map((section, index) => (
              <div key={index} className="flex flex-col gap-2">
                <h3 className="text-base font-semibold text-txt-primary">{section.title}</h3>
                {Array.isArray(section.content) ? (
                  <ul className="flex flex-col gap-2">
                    {section.content.map((item, i) => (
                      <li key={i} className="text-sm leading-relaxed text-txt-secondary">
                        • {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm leading-relaxed text-txt-secondary">{section.content}</p>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => modalClockOutInfo.current?.dismiss()}
            className="w-full rounded-xl bg-surface-tertiary py-3 text-center text-base font-semibold text-txt-primary"
          >
            {t("common:close")}
          </button>
        </div>
      </ModalSheetAutoHeight>
    </>
  );
}
